import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { COMPANY } from "@/mock";
import ContactForm from "@/components/forms/ContactForm";
import QuoteForm from "@/components/forms/QuoteForm";

const ContactPage = () => {
  return (
    <div data-testid="contact-page" className="bg-brand-bg">
      {/* Header */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-6 flex items-center gap-3">
                <span className="block w-10 h-px bg-brand-cta" />
                Contact
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-brand-ink leading-[0.95]">
                Let's talk about <br />
                <span className="italic font-light">your project.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-base lg:text-lg text-brand-inkSoft leading-relaxed">
                Reach out for a quick question, or scroll down to request a
                detailed estimate. We respond within one business day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Strip */}
      <section className="border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border">
          <ContactCard
            icon={Phone}
            label="Call us"
            value={COMPANY.phone}
            href={`tel:${COMPANY.phone.replace(/[^0-9+]/g, "")}`}
            testid="contact-info-phone"
          />
          <ContactCard
            icon={Mail}
            label="Email"
            value={COMPANY.email}
            href={`mailto:${COMPANY.email}`}
            testid="contact-info-email"
          />
          <ContactCard
            icon={MapPin}
            label="Service Area"
            value={COMPANY.address}
            testid="contact-info-area"
          />
          <ContactCard
            icon={Clock}
            label="Hours"
            value={COMPANY.hours}
            testid="contact-info-hours"
          />
        </div>
      </section>

      {/* Forms */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Quick Contact */}
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-4">
              General Inquiry
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-brand-ink mb-4 leading-[1]">
              Quick question?
            </h2>
            <p className="text-sm text-brand-inkSoft leading-relaxed mb-8">
              Send a message and we'll reply within one business day. For a
              detailed project estimate, use the form on the right.
            </p>
            <ContactForm />
          </div>

          {/* Quote Request */}
          <div className="lg:col-span-7 bg-brand-surface border border-brand-border p-8 lg:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-4">
              Free Estimate
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-brand-ink mb-4 leading-[1]">
              Request a quote.
            </h2>
            <p className="text-sm text-brand-inkSoft leading-relaxed mb-8">
              The more detail you share, the more accurate our estimate. We'll
              follow up to schedule a free walkthrough.
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>
    </div>
  );
};

const ContactCard = ({ icon: Icon, label, value, href, testid }) => {
  const content = (
    <div className="flex items-start gap-4 h-full">
      <div className="w-10 h-10 border border-brand-cta flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-brand-cta" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft mb-1.5">
          {label}
        </p>
        <p className="text-sm font-semibold text-brand-ink truncate">
          {value}
        </p>
      </div>
    </div>
  );
  return href ? (
    <a
      href={href}
      data-testid={testid}
      className="bg-brand-bg p-8 hover:bg-brand-subtle transition-colors block"
    >
      {content}
    </a>
  ) : (
    <div data-testid={testid} className="bg-brand-bg p-8">
      {content}
    </div>
  );
};

export default ContactPage;
